<template>
	<template v-if="auth.user?.role === 'admin' && auth.hasFeatureFlag('email-delivery')">
		<Button
			v-if="compact"
			v-tooltip.top="'Email download link'"
			aria-label="Email download link"
			icon="fal fa-envelope"
			rounded
			text
			@click.stop="open" />
		<Button
			v-else
			icon="fal fa-envelope"
			label="Email download link"
			outlined
			severity="secondary"
			@click="open" />
		<FormContainer
			as-dialog
			class="send-email-download"
			title="Email download link"
			:visible
			@close="close">
			<form
				v-if="!download && (template || loading)"
				class="send-email-download__body"
				@submit.prevent="send">
				<p
					v-if="template"
					class="send-email-download__muted">
					{{ template.app_name
					}}<template v-if="template.build_version !== null">
						· v{{ template.build_version }}</template
					>
				</p>
				<FormInput
					label="Recipient"
					required>
					<InputText
						ref="recipientInput"
						v-model="form.recipient"
						autofocus
						fluid
						maxlength="254"
						required
						type="email"
						:disabled="!template || sending" />
				</FormInput>
				<FormInput
					label="Subject"
					required>
					<InputText
						v-model="form.subject"
						fluid
						maxlength="255"
						required
						:disabled="!template || sending" />
				</FormInput>
				<FormInput
					label="Message"
					required>
					<Textarea
						v-model="form.message"
						auto-resize
						fluid
						maxlength="10000"
						required
						rows="5"
						:disabled="!template || sending" />
				</FormInput>
				<p class="send-email-download__muted">
					The email adds a download button, the expiry date and sign-in steps below your message. No
					tokens are charged.
				</p>
				<Message
					v-if="error"
					severity="error"
					size="small">
					{{ error }}
				</Message>
				<div class="send-email-download__footer">
					<Button
						label="Cancel"
						severity="secondary"
						text
						:disabled="sending"
						@click="close" />
					<Button
						icon="fal fa-paper-plane"
						label="Send email"
						type="submit"
						:loading="loading || sending" />
				</div>
			</form>
			<div
				v-else-if="!download"
				class="send-email-download__body">
				<Message
					severity="error"
					size="small">
					{{ error }}
				</Message>
				<div class="send-email-download__footer">
					<Button
						label="Close"
						@click="close" />
				</div>
			</div>
			<div
				v-else
				class="send-email-download__body">
				<div
					class="send-email-download__progress"
					role="status">
					<span class="send-email-download__recipient">To {{ download.recipient }}</span>
					<EmailDownloadStatus :download />
				</div>
				<Message
					v-if="download.status === 'failed'"
					severity="error"
					size="small">
					{{ download.failure_reason }}
				</Message>
				<p
					v-else
					class="send-email-download__muted">
					{{ progressText }}
				</p>
				<Message
					v-if="pollingError"
					severity="warn"
					size="small">
					Can't refresh the status. Retrying…
				</Message>
				<div class="send-email-download__footer">
					<Button
						label="View history"
						severity="secondary"
						text
						@click="viewHistory" />
					<Button
						v-if="!inProgress"
						label="Send another"
						outlined
						@click="sendAnother" />
					<Button
						label="Close"
						@click="close" />
				</div>
			</div>
		</FormContainer>
	</template>
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, computed, nextTick, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { format } from 'date-fns'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Textarea from 'primevue/textarea'
import FormContainer from '@/components/FormContainer.vue'
import FormInput from '@/components/FormInput.vue'
import EmailDownloadStatus from './EmailDownloadStatus.vue'
import { useAuthStore } from '@/stores/Auth'
import type {
	EmailDownload,
	EmailDownloadSelection,
	EmailDownloadTemplate,
} from '@/models/EmailDownload/Model'
import EmailDownloadsApi from '@/models/EmailDownload/Api'
import { emailDownloadError } from '@/models/EmailDownload/Errors'

const props = defineProps<{
	appId?: number | string
	buildId?: number | string
	compact?: boolean
}>()
const auth = useAuthStore()
const router = useRouter()
const api = new EmailDownloadsApi()
const visible = ref(false)
const loading = ref(false)
const sending = ref(false)
const error = ref('')
const pollingError = ref(false)
const template = ref<EmailDownloadTemplate | null>(null)
const form = ref({ recipient: '', subject: '', message: '' })
const download = ref<EmailDownload | null>(null)
const recipientInput = ref<ComponentPublicInstance<typeof InputText> | null>(null)
const inProgress = computed(
	() => !!download.value && ['queued', 'generating', 'sending'].includes(download.value.status),
)
const progressText = computed(() => {
	if (inProgress.value)
		return 'Generating the ZIP and sending the email. You can close this window.'
	if (download.value?.status === 'revoked') return 'This link was revoked.'
	return `The link expires ${format(new Date(download.value!.expires_at!), 'PPpp')}.`
})
let timer: ReturnType<typeof setTimeout> | undefined
let unmounted = false
// Bumped on every template load and reset, so a response for a closed form is ignored.
let templateRequest = 0

onUnmounted(() => {
	unmounted = true
	templateRequest++
	clearTimeout(timer)
})

function selection(): EmailDownloadSelection {
	return props.buildId !== undefined ? { build_id: props.buildId } : { app_id: props.appId! }
}

async function open() {
	visible.value = true
	if (download.value) await refresh()
	else await loadTemplate()
}

function close() {
	// The send request answers quickly (the work is queued), so the dialog stays open until its result is shown.
	if (sending.value) return
	visible.value = false
	clearTimeout(timer)
	// A finished request is done with; the next open starts a fresh form.
	if (!inProgress.value) reset()
}

function reset() {
	templateRequest++
	download.value = null
	template.value = null
	loading.value = false
	error.value = ''
	pollingError.value = false
}

async function loadTemplate() {
	const request = ++templateRequest
	loading.value = true
	error.value = ''
	template.value = null
	try {
		const result = (await api.template(selection())).data
		if (request !== templateRequest) return
		template.value = result
		form.value = { recipient: result.recipient, subject: result.subject, message: result.message }
		loading.value = false
		await nextTick()
		recipientInput.value?.$el.focus()
	} catch (exception) {
		const message = await emailDownloadError(exception)
		if (request !== templateRequest) return
		error.value = message
		loading.value = false
	}
}

async function send() {
	if (!template.value || sending.value) return
	sending.value = true
	error.value = ''
	try {
		download.value = (
			await api.store({
				...selection(),
				expected_build_id: template.value.build_id,
				...form.value,
			})
		).data
		schedule()
	} catch (exception) {
		error.value = await emailDownloadError(exception)
	} finally {
		sending.value = false
	}
}

async function refresh() {
	const id = download.value!.id
	try {
		const result = (await api.show(id)).data
		if (download.value?.id === id) download.value = result
		pollingError.value = false
	} catch {
		pollingError.value = true
	}
	schedule()
}

function schedule() {
	clearTimeout(timer)
	if (!unmounted && visible.value && inProgress.value) timer = setTimeout(refresh, 2000)
}

function sendAnother() {
	reset()
	void loadTemplate()
}

function viewHistory() {
	const appId = download.value!.app_id
	close()
	router.push({ name: 'admin-email-downloads', query: { app_id: appId ?? undefined } })
}
</script>

<style scoped lang="scss">
.send-email-download {
	.send-email-download__body {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.send-email-download__muted {
		margin: 0;
		font-size: 13px;
		color: var(--p-text-muted-color);
	}

	.send-email-download__progress {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;

		.send-email-download__recipient {
			font-weight: 600;
			overflow-wrap: anywhere;
		}
	}

	.send-email-download__footer {
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-end;
		gap: 10px;
	}
}
</style>
